import { FC, memo, useCallback, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '../../../redux/hooks'
import { ListContainer, CardsRow } from './styles'
import RecentSearchCard from './card'
import RecentSearchesSkeleton from './skeleton'
import { getSearchbarHistoryItemsState } from '../../../redux/searchbar/selectors'
import { SearchbarHistoryItem } from '../../../redux/searchbar/types'
import { saveHistoryItem } from '../../../redux/searchbar/actions'
import { useTranslation } from '../../../hooks/locale'
import { useABVariant } from '../../../utils/hooks'
import { AB_KEY, AB_VARIANT_VERSION } from '../../../common/constants'
import { format as formatDateFns } from 'date-fns'
import { getSearchDate } from '../../../utils/miscellaneous-utils'
import { useSearchUrl } from '../../../hooks/search'
import { getFilterFromHref } from '../../../utils/request-utils'
import AnalyticsService from '../../../services/analytics-service'
import { cardEventCategories } from '../../../services/analytics-service/categories'
import { Section, TitleContainer, Title, Subtitle } from '../../home/common/styles'

const getItemKey = (item: SearchbarHistoryItem): string =>
    `${item.search}|${item.arrival ? formatDateFns(item.arrival, 'ddMMyy') : ''}|${
        item.departure ? formatDateFns(item.departure, 'ddMMyy') : ''
    }|${item.adults}|${item.children}`

interface Props {
    title?: string
    subtitle?: string
}

const RecentSearchesWidget: FC<Props> = ({
    title = 'pages.home.recentSearches.heading',
    subtitle = 'pages.home.recentSearches.subheading',
}) => {
    const dispatch = useAppDispatch()
    const liveHistoryItems = useSelector(getSearchbarHistoryItemsState)
    const { t } = useTranslation()
    const { getSearchUrl } = useSearchUrl()

    const { isProvidedVariantActive: shouldShowWidget, isLoading: isABLoading } = useABVariant(
        AB_KEY.RECENT_SEARCHES,
        AB_VARIANT_VERSION.ONE,
    )

    const [loadingItemKey, setLoadingItemKey] = useState<string | null>(null)

    const historyItemsRef = useRef<SearchbarHistoryItem[]>(liveHistoryItems)
    if (historyItemsRef.current.length === 0 && liveHistoryItems.length > 0) {
        historyItemsRef.current = liveHistoryItems
    }
    const historyItems = historyItemsRef.current

    const _handleHistoryItemSelect = useCallback(
        async (item: SearchbarHistoryItem) => {
            dispatch(saveHistoryItem(item))
            setLoadingItemKey(getItemKey(item))

            try {
                const searchUrl = await getSearchUrl(getFilterFromHref(globalThis.location.href), {
                    regionId: null,
                    search: item.search,
                    arrival: getSearchDate(item.arrival),
                    departure: getSearchDate(item.departure),
                    adults: item.adults,
                    children: item.children,
                })

                const fullUrl = `${t('traumBaseUrl')}${searchUrl}`

                try {
                    AnalyticsService.trackClickEvent(cardEventCategories.sections.RECENT_SEARCHES, searchUrl)
                } catch (analyticsError) {
                    console.error('Analytics tracking failed:', analyticsError)
                }
                setLoadingItemKey(null)
                globalThis.location.href = fullUrl
            } catch (error) {
                console.error('Failed to navigate to search results:', error)
                setLoadingItemKey(null)
            }
        },
        [dispatch, getSearchUrl, t],
    )

    if (historyItems.length === 0) {
        return null
    }

    if (isABLoading) {
        return (
            <Section>
                <RecentSearchesSkeleton TitleContainer={TitleContainer} />
            </Section>
        )
    }

    if (!shouldShowWidget) {
        return null
    }

    return (
        <Section>
            <TitleContainer>
                <Title>{t(title)}</Title>
                <Subtitle>{t(subtitle)}</Subtitle>
            </TitleContainer>
            <ListContainer>
                <CardsRow>
                    {historyItems.map(item => (
                        <RecentSearchCard
                            key={`recent-searches-${item._time}`}
                            historyItem={item}
                            onSelect={_handleHistoryItemSelect}
                            isLoading={loadingItemKey === getItemKey(item)}
                        />
                    ))}
                </CardsRow>
            </ListContainer>
        </Section>
    )
}

export default memo(RecentSearchesWidget)
